<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useTranslation } from "i18next-vue";
import type { BlockAttributeDefInfo } from "@/drawing/blocks";
import DRDialog from "@/components/UI/feedbacks/DRDialog.vue";

/**
 * Pre-insert form for a block carrying attributes: one field per editable ATTDEF,
 * prefilled with the definition's default text. Confirming arms the block tool
 * with these values; closing without confirming aborts the selection. Each placed
 * instance stays editable afterwards through the attributes panel.
 *
 * Tags and prompts come from the customer's DWG and are shown as authored,
 * never translated.
 */
const props = defineProps<{
  modelValue: boolean;
  blockName: string;
  attributes: BlockAttributeDefInfo[];
  /** Values already entered for this block — they win over the definition's defaults. */
  initialValues?: Record<string, string> | null;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "confirm", values: Record<string, string>): void;
}>();

const { t } = useTranslation();

/** Edited values, keyed by tag — reset from the definitions on every open. */
const drafts = ref<Record<string, string>>({});

watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) return;
    drafts.value = Object.fromEntries(
      props.attributes.map((attr) => [
        attr.tag,
        props.initialValues?.[attr.tag] ?? attr.defaultValue,
      ]),
    );
  },
  { immediate: true },
);

const show = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

function handleConfirm(): void {
  emit("confirm", { ...drafts.value });
  show.value = false;
}
</script>

<template>
  <DRDialog
    v-model="show"
    class="canvas-dialog"
    :width="420"
    :title="t('canvas:settings.blocks.attributesModal.title')"
    :confirm-label="t('canvas:settings.blocks.attributesModal.confirm')"
    @confirm="handleConfirm"
  >
    <p class="modal-hint">
      {{
        t("canvas:settings.blocks.attributesModal.description", {
          name: blockName,
        })
      }}
    </p>

    <div v-for="attr in attributes" :key="attr.tag" class="mb-3">
      <label :for="`block-attr-${attr.tag}`" class="form-label attr-label">
        {{ attr.tag }}
      </label>
      <input
        :id="`block-attr-${attr.tag}`"
        v-model="drafts[attr.tag]"
        type="text"
        class="form-control"
        @keyup.enter="handleConfirm"
      />
      <small v-if="attr.prompt" class="attr-prompt">{{ attr.prompt }}</small>
    </div>
  </DRDialog>
</template>

<style lang="scss" scoped>
.modal-hint {
  margin-bottom: 1rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
}

.attr-label {
  margin-bottom: 0.25rem;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.75);
  overflow-wrap: anywhere;
}

.attr-prompt {
  display: block;
  margin-top: 0.25rem;
  color: rgba(255, 255, 255, 0.45);
}
</style>
